<script lang="ts">
  import { AatmanovaClient } from '@aatmanova/sdk';
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { derived } from 'svelte/store';
  import { Badge, Skeleton } from '@aatmanova/ui';

  interface Props {
    client: AatmanovaClient;
    addToast: (msg: string, type: 'info' | 'success' | 'warning' | 'error') => void;
  }

  let { client, addToast }: Props = $props();
  const queryClient = useQueryClient();

  // Selected quote for detail drawer
  let selectedQuote = $state<any | null>(null);
  let statusFilter = $state<string>('ALL');
  let searchQuery = $state<string>('');

  // Fetch Quotes Query
  const quotesQuery = createQuery({
    queryKey: ['admin_quotes'],
    queryFn: async () => {
      const res = await client.getQuotes();
      return res.data || [];
    }
  });

  // Mutators
  const updateStatusMutation = createMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      return client.updateQuoteStatus(id, status as any);
    },
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['admin_quotes'] });
      addToast('Quote status updated successfully.', 'success');
      // Refresh current selection details
      if (selectedQuote && res.data) {
        selectedQuote = { ...selectedQuote, status: res.data.status };
      }
    },
    onError: (err: any) => {
      addToast(err.message || 'Failed to update quote status.', 'error');
    }
  });

  const convertToOrderMutation = createMutation({
    mutationFn: async (id: string) => {
      return client.convertQuoteToOrder(id);
    },
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['admin_quotes'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      addToast('Quote successfully converted to live order.', 'success');
      if (selectedQuote) {
        selectedQuote = { ...selectedQuote, status: 'CONVERTED' };
      }
    },
    onError: (err: any) => {
      addToast(err.message || 'Conversion to order failed.', 'error');
    }
  });

  const generatePdfMutation = createMutation({
    mutationFn: async (id: string) => {
      return client.generateQuotePdf(id);
    },
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['admin_quotes'] });
      addToast('Proforma PDF generated and stored in cloud.', 'success');
      if (selectedQuote && res.data) {
        selectedQuote = { ...selectedQuote, pdfUrl: res.data.pdfUrl };
      }
    },
    onError: (err: any) => {
      addToast(err.message || 'Failed to generate proforma PDF.', 'error');
    }
  });

  // Derived filtered quotes list
  const filteredQuotes = $derived.by(() => {
    const list = $quotesQuery.data || [];
    const query = searchQuery.trim().toLowerCase();

    return list.filter((q: any) => {
      // Status Filter
      if (statusFilter !== 'ALL' && q.status !== statusFilter) {
        return false;
      }

      // Search Query
      if (query) {
        const name = (q.name || '').toLowerCase();
        const email = (q.email || '').toLowerCase();
        const contact = (q.contactNo || '').toLowerCase();
        const location = (q.location || '').toLowerCase();
        const message = (q.message || '').toLowerCase();
        const quoteId = (q.id || '').toLowerCase();

        return name.includes(query) ||
               email.includes(query) ||
               contact.includes(query) ||
               location.includes(query) ||
               message.includes(query) ||
               quoteId.includes(query);
      }

      return true;
    });
  });

  function selectQuote(quote: any) {
    selectedQuote = quote;
  }

  function getStatusBadgeVariant(status: string) {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'APPROVED': return 'info';
      case 'CONVERTED': return 'success';
      case 'REJECTED': return 'error';
      case 'EXPIRED': return 'neutral';
      default: return 'neutral';
    }
  }
</script>

<div class="space-y-6">
  <!-- Title / Intro -->
  <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
    <div class="space-y-1">
      <h2 class="text-3xl font-extrabold tracking-tight text-base-content font-display">Quote Registry</h2>
      <p class="text-sm text-base-content/70">Review customer proforma inquiries, issue approvals, and convert orders.</p>
    </div>
  </div>

  <!-- Search & Filter Controls -->
  <div class="grid grid-cols-1 md:grid-cols-3 gap-4 items-center bg-base-100 border border-base-300 p-4 rounded-xl shadow-md">
    <!-- Search field -->
    <div class="relative md:col-span-2">
      <input 
        type="text" 
        bind:value={searchQuery}
        placeholder="Search quotes by client name, email, contact number or location..."
        class="input input-bordered w-full pl-10 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-accent"
      />
      <div class="absolute left-3.5 top-3.5 text-base-content/40">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4.5 w-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
    </div>

    <!-- Status dropdown filter -->
    <div class="w-full">
      <select bind:value={statusFilter} class="select select-bordered w-full text-xs font-bold focus:outline-none focus:ring-1 focus:ring-accent">
        <option value="ALL">All Statuses</option>
        <option value="PENDING">Pending Approval</option>
        <option value="APPROVED">Approved (Pending Conversion)</option>
        <option value="CONVERTED">Converted to Order</option>
        <option value="REJECTED">Rejected</option>
        <option value="EXPIRED">Expired</option>
      </select>
    </div>
  </div>

  <!-- Main Table Section -->
  {#if $quotesQuery.isLoading}
    <div class="space-y-4">
      <Skeleton height="h-12" />
      <Skeleton height="h-16" />
      <Skeleton height="h-16" />
      <Skeleton height="h-16" />
    </div>
  {:else if !$quotesQuery.data || filteredQuotes.length === 0}
    <div class="py-20 text-center border border-dashed border-base-300 rounded-2xl bg-base-100/50">
      <p class="text-base-content/50 font-medium">No quotes found matching current filter queries.</p>
    </div>
  {:else}
    <!-- Desktop Table View -->
    <div class="hidden md:block bg-base-100 border border-base-300 rounded-xl overflow-hidden shadow-xl">
      <div class="overflow-x-auto">
        <table class="table table-zebra table-md w-full">
          <thead>
            <tr class="bg-base-200 text-xs font-bold text-base-content/70 uppercase tracking-wider">
              <th>Quote ID</th>
              <th>Client Info</th>
              <th>Date Filed</th>
              <th>Location</th>
              <th>Items</th>
              <th>Est. Grand Total</th>
              <th>Status</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="text-base-content/90">
            {#each filteredQuotes as quote}
              <tr class="hover cursor-pointer" onclick={() => selectQuote(quote)}>
                <td class="font-mono text-xs font-black text-accent">
                  #{quote.id.slice(-8).toUpperCase()}
                </td>
                <td>
                  <div class="font-bold text-xs">{quote.name}</div>
                  <div class="text-[10px] text-base-content/55 font-semibold">{quote.contactNo}</div>
                </td>
                <td class="text-xs font-semibold text-base-content/75">
                  {new Date(quote.createdAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                </td>
                <td class="text-xs font-bold text-base-content/70">
                  {quote.location}
                </td>
                <td class="text-xs text-base-content/65 font-bold">
                  {quote.items?.length || 0} unique items
                </td>
                <td class="font-black text-xs">
                  <span class="text-[10px] opacity-75 font-bold">Rs.</span> {Number(quote.total).toFixed(2)}
                </td>
                <td>
                  <Badge
                    variant={getStatusBadgeVariant(quote.status)}
                    size="xs"
                    class="badge-outline font-black uppercase tracking-wider"
                  >
                    {quote.status}
                  </Badge>
                </td>
                <td class="text-right" onclick={(e) => e.stopPropagation()}>
                  <button 
                    onclick={() => selectQuote(quote)}
                    class="btn btn-ghost btn-xs font-black text-accent uppercase tracking-wider"
                  >
                    Manage
                  </button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>

    <!-- Mobile Card View -->
    <div class="md:hidden space-y-4">
      {#each filteredQuotes as quote}
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
        <div 
          class="card bg-base-100 border border-base-300 rounded-2xl p-4 shadow-sm hover:border-primary/30 transition-all duration-300 space-y-3 cursor-pointer"
          onclick={() => selectQuote(quote)}
        >
          <!-- Card Header (Quote ID, Status & Manage Button) -->
          <div class="flex items-center justify-between">
            <span class="font-mono text-xs font-black text-accent">
              #{quote.id.slice(-8).toUpperCase()}
            </span>
            <div class="flex items-center gap-2">
              <Badge
                variant={getStatusBadgeVariant(quote.status)}
                size="xs"
                class="badge-outline font-black uppercase tracking-wider"
              >
                {quote.status}
              </Badge>
              <button 
                onclick={(e) => { e.stopPropagation(); selectQuote(quote); }}
                class="btn btn-ghost btn-xs font-black text-accent uppercase tracking-wider text-[10px]"
              >
                Manage
              </button>
            </div>
          </div>

          <!-- Client Info & Location -->
          <div class="grid grid-cols-2 gap-2 text-[11px] pt-1">
            <div>
              <span class="block text-[9px] font-bold text-base-content/40 uppercase tracking-wider mb-0.5">Client</span>
              <span class="font-bold text-base-content block">{quote.name}</span>
              <span class="text-base-content/55 font-semibold block">{quote.contactNo}</span>
            </div>
            <div>
              <span class="block text-[9px] font-bold text-base-content/40 uppercase tracking-wider mb-0.5">Location</span>
              <span class="font-bold text-base-content block">{quote.location}</span>
            </div>
          </div>

          <!-- Items, Date Filed & Grand Total -->
          <div class="grid grid-cols-2 gap-2 pt-2.5 border-t border-base-200 text-[11px]">
            <div>
              <span class="block text-[9px] font-bold text-base-content/40 uppercase tracking-wider mb-0.5">Date / Volume</span>
              <span class="font-semibold text-base-content/75 block">
                {new Date(quote.createdAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
              </span>
              <span class="text-[10px] text-base-content/60 font-semibold block">
                {quote.items?.length || 0} unique item(s)
              </span>
            </div>
            <div>
              <span class="block text-[9px] font-bold text-base-content/40 uppercase tracking-wider mb-0.5">Est. Total</span>
              <span class="font-black text-xs text-accent">Rs. {Number(quote.total).toFixed(2)}</span>
            </div>
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>

<!-- Sliding Details Drawer Overlay -->
{#if selectedQuote}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
  <div 
    role="presentation"
    class="fixed inset-0 bg-black/60 backdrop-blur-xs z-[100] transition-opacity duration-300"
    onclick={() => selectedQuote = null}
  ></div>

  <div 
    class="fixed top-0 right-0 h-screen w-full max-w-2xl bg-base-100 border-l border-base-300 shadow-2xl flex flex-col z-[101] transition-transform duration-300 overflow-y-auto"
  >
    <!-- Drawer Header -->
    <div class="p-6 border-b border-base-300 flex items-center justify-between bg-base-200/50">
      <div class="space-y-1">
        <div class="flex items-center gap-2">
          <span class="font-mono text-sm font-black text-accent">#{selectedQuote.id.toUpperCase()}</span>
          <Badge
            variant={getStatusBadgeVariant(selectedQuote.status)}
            size="xs"
            class="badge-outline font-black uppercase tracking-widest"
          >
            {selectedQuote.status}
          </Badge>
        </div>
        <p class="text-[10px] text-base-content/60 font-semibold">Registered: {new Date(selectedQuote.createdAt).toLocaleString()}</p>
      </div>
      <button 
        onclick={() => selectedQuote = null}
        class="btn btn-sm btn-circle btn-ghost"
      >✕</button>
    </div>

    <!-- Drawer Body -->
    <div class="flex-grow p-6 space-y-6">
      <!-- Patron details card -->
      <div class="bg-base-200/60 rounded-xl p-4 border border-base-300/80 space-y-3">
        <h3 class="text-xs font-black uppercase tracking-wider text-base-content/75 mb-1.5">Patron & Destination</h3>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <span class="block text-[9px] font-black text-base-content/50 uppercase">Name</span>
            <span class="text-xs font-bold">{selectedQuote.name}</span>
          </div>
          <div>
            <span class="block text-[9px] font-black text-base-content/50 uppercase">Email</span>
            <span class="text-xs font-bold text-accent">{selectedQuote.email || 'N/A'}</span>
          </div>
          <div>
            <span class="block text-[9px] font-black text-base-content/50 uppercase">Contact Number</span>
            <span class="text-xs font-bold">{selectedQuote.contactNo}</span>
          </div>
          <div>
            <span class="block text-[9px] font-black text-base-content/50 uppercase">Destination Location</span>
            <span class="text-xs font-bold">{selectedQuote.location}</span>
          </div>
        </div>

        {#if selectedQuote.message}
          <div class="border-t border-base-300 pt-3 mt-1.5">
            <span class="block text-[9px] font-black text-base-content/50 uppercase">Client Notes</span>
            <p class="text-xs font-medium text-base-content/80 mt-1 leading-relaxed bg-base-100 p-2.5 rounded-lg border border-base-300/50">
              "{selectedQuote.message}"
            </p>
          </div>
        {/if}
      </div>

      <!-- Itemization -->
      <div class="space-y-3">
        <h3 class="text-xs font-black uppercase tracking-wider text-base-content/75">Itemization</h3>
        <div class="border border-base-300 rounded-xl overflow-x-auto scrollbar-none">
          <table class="table table-zebra table-sm w-full">
            <thead>
              <tr class="bg-base-200/80 text-[10px] font-bold text-base-content/65 uppercase tracking-wider">
                <th>Product Description</th>
                <th class="text-center">Qty</th>
                <th class="text-right">Unit Price</th>
                <th class="text-right font-bold">Total</th>
              </tr>
            </thead>
            <tbody>
              {#each selectedQuote.items as item}
                <tr>
                  <td>
                    <div class="font-bold text-xs">{item.product?.name || `Product: ${item.productId}`}</div>
                    <div class="text-[9px] text-base-content/50 font-mono">{item.product?.sku || 'N/A'}</div>
                  </td>
                  <td class="text-center text-xs font-bold">{item.quantity}</td>
                  <td class="text-right text-xs font-semibold">₹{Number(item.product?.offerPrice || 0).toFixed(2)}</td>
                  <td class="text-right text-xs font-bold">₹{(Number(item.product?.offerPrice || 0) * item.quantity).toFixed(2)}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>

      <!-- Totals & Price Calculations -->
      <div class="flex justify-end">
        <div class="w-full sm:w-72 bg-base-200/40 p-4 border border-base-300 rounded-xl space-y-2 text-xs">
          <div class="flex justify-between font-medium">
            <span class="text-base-content/60">Subtotal:</span>
            <span>₹{Number(selectedQuote.total).toFixed(2)}</span>
          </div>
          {#if Number(selectedQuote.discountAmount) > 0}
            <div class="flex justify-between font-medium text-red-500">
              <span>Discount applied:</span>
              <span>- ₹{Number(selectedQuote.discountAmount).toFixed(2)}</span>
            </div>
          {/if}
          <div class="flex justify-between font-medium">
            <span class="text-base-content/60">Estimated Taxes (12%):</span>
            <span>₹{((Number(selectedQuote.total) - Number(selectedQuote.discountAmount || 0)) * 0.12).toFixed(2)}</span>
          </div>
          <div class="border-t border-base-300 pt-2 flex justify-between font-black text-sm text-accent uppercase tracking-wider">
            <span>Grand Total:</span>
            <span>₹{((Number(selectedQuote.total) - Number(selectedQuote.discountAmount || 0)) * 1.12).toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Drawer Actions Footer -->
    <div class="p-6 border-t border-base-300 bg-base-200/50 flex flex-wrap gap-3 items-center justify-between shrink-0">
      <!-- Left side: Cancel/Reject buttons -->
      <div class="flex gap-2">
        {#if selectedQuote.status === 'PENDING'}
          <button 
            disabled={$updateStatusMutation.isPending}
            onclick={() => $updateStatusMutation.mutate({ id: selectedQuote.id, status: 'REJECTED' })}
            class="btn btn-sm btn-error btn-outline font-bold uppercase tracking-wider text-[10px]"
          >
            Reject Request
          </button>
        {/if}
      </div>

      <!-- Right side: Approve / Generate PDF / Convert buttons -->
      <div class="flex gap-2">
        {#if selectedQuote.status === 'PENDING'}
          <button 
            disabled={$updateStatusMutation.isPending}
            onclick={() => $updateStatusMutation.mutate({ id: selectedQuote.id, status: 'APPROVED' })}
            class="btn btn-sm btn-accent font-bold uppercase tracking-wider text-[10px] text-white"
          >
            Approve (Mark Pending Conversion)
          </button>
        {/if}

        {#if selectedQuote.status === 'APPROVED'}
          <!-- PDF Generator -->
          {#if selectedQuote.pdfUrl}
            <a 
              href={selectedQuote.pdfUrl} 
              target="_blank" 
              class="btn btn-sm btn-outline btn-primary font-bold uppercase tracking-wider text-[10px]"
            >
              Download PDF Proforma
            </a>
          {:else}
            <button 
              disabled={$generatePdfMutation.isPending}
              onclick={() => $generatePdfMutation.mutate(selectedQuote.id)}
              class="btn btn-sm btn-outline btn-primary font-bold uppercase tracking-wider text-[10px]"
            >
              {#if $generatePdfMutation.isPending}
                Generating PDF...
              {:else}
                Generate Proforma PDF
              {/if}
            </button>
          {/if}

          <!-- Convert to live order -->
          <button 
            disabled={$convertToOrderMutation.isPending}
            onclick={() => $convertToOrderMutation.mutate(selectedQuote.id)}
            class="btn btn-sm btn-success font-bold uppercase tracking-wider text-[10px] text-white"
          >
            {#if $convertToOrderMutation.isPending}
              Converting...
            {:else}
              Convert to Live Order
            {/if}
          </button>
        {/if}

        {#if selectedQuote.status === 'CONVERTED'}
          <span class="text-xs font-black text-emerald-600 dark:text-emerald-500 uppercase tracking-widest flex items-center gap-1">
            ✓ Converted to Order
          </span>
          {#if selectedQuote.pdfUrl}
            <a 
              href={selectedQuote.pdfUrl} 
              target="_blank" 
              class="btn btn-xs btn-outline btn-neutral font-bold uppercase tracking-wider text-[10px]"
            >
              Proforma Copy
            </a>
          {/if}
        {/if}
      </div>
    </div>
  </div>
{/if}
